import { AxiosError } from 'axios'

/**
 * Extended Error class that preserves structured API error data (code, details, status).
 */
export class ApiError extends Error {
  code?: string
  details?: Record<string, unknown>
  status?: number

  constructor(message: string, code?: string, details?: Record<string, unknown>, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.details = details
    this.status = status
  }
}

/**
 * Extracts the server-provided error message from an API error, falling back
 * to the given default. For hooks that surface the raw message in local state.
 */
export function extractErrorMessage(error: unknown, defaultMessage: string): string {
  const err = error as Error & { response?: { data?: { message?: string } } }
  return err.response?.data?.message || defaultMessage
}

/**
 * Error message configuration for domain-specific errors
 */
export interface ErrorMessageConfig {
  /** Message for 403 Forbidden responses */
  forbidden?: string
  /** Message for 404 Not Found responses */
  notFound?: string
  /** Message for 409 Conflict responses */
  conflict?: string
  /** Message for network errors (no response received) */
  networkError?: string
  /** Fallback message when no specific message available */
  defaultMessage: string
}

/**
 * Default error messages used when service doesn't specify custom ones
 */
const DEFAULT_ERROR_MESSAGES = {
  forbidden: 'You do not have permission to perform this action',
  notFound: 'Resource not found',
  networkError: 'Network error. Please check your connection and try again.'
}

/**
 * Transforms an AxiosError into a user-friendly Error based on config
 */
function createApiError(error: AxiosError, config: ErrorMessageConfig): Error {
  if (error.response) {
    const { status, data } = error.response
    const responseData = data as Record<string, unknown> | undefined
    const serverMessage = responseData?.message as string | undefined

    switch (status) {
      case 400: {
        const code400 = responseData?.code as string | undefined
        const details400 = responseData?.details as Record<string, unknown> | undefined
        if (code400) {
          return new ApiError(serverMessage || 'Invalid request', code400, details400, 400)
        }
        return new Error(serverMessage || 'Invalid request')
      }
      case 401:
        // Use defaultMessage for 401 errors - the generic "Authentication failed"
        // from the server is misleading for non-auth endpoints
        return new Error(config.defaultMessage)
      case 403:
        return new Error(config.forbidden || DEFAULT_ERROR_MESSAGES.forbidden)
      case 404:
        return new Error(config.notFound || DEFAULT_ERROR_MESSAGES.notFound)
      case 409: {
        const code = responseData?.code as string | undefined
        const details = responseData?.details as Record<string, unknown> | undefined
        return new ApiError(
          config.conflict || serverMessage || config.defaultMessage,
          code,
          details,
          409
        )
      }
      default:
        return new Error(serverMessage || config.defaultMessage)
    }
  } else if (error.request) {
    return new Error(config.networkError || DEFAULT_ERROR_MESSAGES.networkError)
  }
  return new Error(error.message || config.defaultMessage)
}

/**
 * Options for the API wrapper
 */
export interface ApiWrapperOptions {
  /** Custom error message configuration */
  errorMessages: ErrorMessageConfig
  /** Log context for console.error (e.g., "Failed to fetch competitions") */
  logContext?: string
  /** If true, CanceledError is rethrown without transformation */
  allowCancel?: boolean
}

/**
 * Wraps an async function with standardized error handling.
 *
 * @example
 * const getCompetitions = withApiErrorHandling(
 *   async () => {
 *     const response = await apiClient.get<Competition[]>('/competitions')
 *     return response.data
 *   },
 *   {
 *     logContext: 'Failed to fetch competitions',
 *     errorMessages: { defaultMessage: 'Failed to load competitions' }
 *   }
 * )
 */
export function withApiErrorHandling<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  options: ApiWrapperOptions
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs): Promise<TResult> => {
    try {
      return await fn(...args)
    } catch (error) {
      // Handle AbortController cancellations
      if (options.allowCancel && (error as Error).name === 'CanceledError') {
        throw error
      }

      // Log error with context
      if (options.logContext) {
        console.error(`${options.logContext}:`, error)
      }

      // Transform and rethrow
      throw createApiError(error as AxiosError, options.errorMessages)
    }
  }
}
